import { Spin } from 'antd';
// biome-ignore lint/style/useImportType: <explanation>
import React, { useMemo, useState, CSSProperties } from 'react';
import styled from 'styled-components';

import { metIntegration } from '@metservicedev/platforms-services-interface';
import { InfoPopover, IssueTime } from '@mono/metui';
import { TextStyles } from '@mono/metui/src/utils/text-styles-selector';
import type { WeatherWidgetRenderProps } from '@mono/types/src/weather-widgets';
import type { IOneMinuteObservation, WeatherStationSettings } from '@mono/types/src/weather-widgets/weather-station-settings';
import { usePlatStageContext } from '@mono/use-plat-stage';

const toolbarStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
};

const Cell = styled.div<{ mode?: 'cozy' | 'compact' }>`
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 12px 0;
    text-align: end;
    min-height: 20px;
    ${({ mode }) => TextStyles[mode ?? 'cozy'].p}

    &:first-child {
        text-align: start;
    }
    &:last-child {
        border-bottom: none;
    }
`;

const Row = styled.div`
    display: flex;
    flex: 1;
    border-bottom: 1px solid #cdcdcd;
    &:last-child {
        border-bottom: none;
    }
    margin: 0px;
    width: 100%;
    justify-content: space-between;
`;

const HeaderCell = styled(Cell)`
    ${({ mode }) => TextStyles[mode ?? 'cozy'].h4}
`;

const TableCell = styled(Cell)`
    white-space: nowrap;

    &:first-child {
        font-weight: 700;
        font-size: 12px;
    }
`;
const Title = styled.h2`
    font-family: 'Open Sans';
    font-style: normal;
    font-weight: 700;
    margin-bottom: 0;
    font-size: 16px;
    color: ${(props) => props.theme.text};
`;
interface IWeatherStationData {
    visibilityAverage1m?: string;
    visibilityAverage10m?: string;
    cloudStatus?: string;
    verboseCloudStatus?: string;
    qnhAverage1m?: number;
    pressureSensorChange1h?: number;
    dewPointAverage1m?: number;
    temperatureAverage1m?: number;
    currentCondition15m?: string;
    issueTime?: string;
}

const getCloudValues = ({ height, coverage }: { height: number; coverage: number | 'NCD' | 'VV' }) => {
    if (coverage === 'NCD') return 'NCD';
    if (coverage === 'VV') return `VV ${height} ft`;
    const cloudAmount = (() => {
        switch (coverage) {
            case 0:
                return 'NCD';
            case 1:
            case 2:
                return 'FEW';
            case 3:
            case 4:
                return 'SCT';
            case 5:
            case 6:
            case 7:
                return 'BKN';
            case 8:
                return 'OVC';
            default:
                return 'Error';
        }
    })();

    if (cloudAmount === 'NCD') return cloudAmount;
    return `${cloudAmount} ${height} ft`;
};

const conditionMapping: { [key: string]: string } = {
    SH: 'Showers',
    TS: 'Thunderstorms',
    FZ: 'Freezing',
    DR: 'Low Drifting',
    BL: 'Blowing',
    DZ: 'Drizzle',
    RA: 'Rain',
    GS: 'Small Hail',
    SN: 'Snow',
    SG: 'Snow Grains',
    BR: 'Mist',
    FG: 'Fog',
    HZ: 'Haze',
    FU: 'Smoke',
    VA: 'Volcanic Ash',
    DU: 'Dust',
    SA: 'Sand',
    SQ: 'Squall',
    PO: 'Dust Whirls',
    FC: 'Funnel Cloud',
    SS: 'Sandstorm',
    DS: 'Duststorm',
};

const getGRAFORValue = (value?: string) => {
    if (!value) return '';
    if (value === '//') return value;
    const severity = (() => {
        if (value[0] === '+') return 'Heavy';
        if (value[0] === '-') return 'Light';
        return 'Moderate';
    })();

    const GRAFORValues = value.replace(/-|\+/g, '').match(/.{1,2}/g) ?? [];

    const conditions = GRAFORValues.map((v) => {
        const condition = conditionMapping[v];
        if (condition) return condition;
        return v;
    });

    return `${severity} ${conditions.join(' ')}`;
};

export const WeatherStation = (props: WeatherWidgetRenderProps<WeatherStationSettings>) => {
    const { isLoading, settings, theme: themeName } = props;
    const { stage } = usePlatStageContext();

    const isDarkMode = themeName === 'dark';
    const theme = isDarkMode
        ? {
              text: '#ffffff',
              issueTime: '#ffffff',
          }
        : {
              text: '#172344',
              issueTime: '#595959',
          };

    const { title, timezone, datasetId, locationLabel } = settings;

    const targetTimezone = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

    const [data, setData] = useState<IWeatherStationData>({
        visibilityAverage1m: undefined,
        visibilityAverage10m: undefined,
        cloudStatus: undefined,
        verboseCloudStatus: undefined,
        qnhAverage1m: undefined,
        pressureSensorChange1h: undefined,
        dewPointAverage1m: undefined,
        temperatureAverage1m: undefined,
        currentCondition15m: undefined,
        issueTime: undefined,
    });

    const { metserviceApi } = metIntegration;

    useMemo(() => {
        if (!datasetId) return;
        const metjwt = localStorage.getItem('metjwt');

        let timeoutHook = 0;
        const abortController = new AbortController();

        const patientQuery = async () => {
            try {
                const headers: { authorization: string } = metjwt
                    ? {
                          authorization: `Bearer ${metjwt}`,
                      }
                    : { authorization: '' };

                await fetch(`${metserviceApi[stage]}/observations/nz/1-minute/weatherStation/${datasetId?.split('.')[1]}/latest`, {
                    headers,
                    signal: abortController.signal,
                })
                    .then(async (res) => {
                        const value = await res.json();

                        const {
                            obs_timestamp,
                            visibil_01mnavg,
                            visibil_10mnavg,
                            cld1amt_30mnavg,
                            cld1hgt_30mnavg,
                            cld2amt_30mnavg,
                            cld2hgt_30mnavg,
                            cld3amt_30mnavg,
                            cld3hgt_30mnavg,
                            pressen_01hrchg,
                            dewtemp_01mnavg,
                            airtemp_01mnavg,
                            presqnh_01mnavg,
                            prwxst1_15mnavg,
                            prwxst2_15mnavg,
                        } = value.results[0] as IOneMinuteObservation;
                        const cloudStatus = [
                            { coverage: cld1amt_30mnavg, height: cld1hgt_30mnavg },
                            { coverage: cld2amt_30mnavg, height: cld2hgt_30mnavg },
                            { coverage: cld3amt_30mnavg, height: cld3hgt_30mnavg },
                        ]
                            .filter(({ height, coverage }) => (height !== null || coverage !== null) && (height !== undefined || coverage !== undefined))
                            .map(getCloudValues)
                            .join(' | ');

                        const verboseCloudStatus =
                            cloudStatus === 'NCD'
                                ? 'No Cloud Detected'
                                : cloudStatus.startsWith('VV')
                                  ? `Vertical Visibility ${cloudStatus.split(' ')[1]} ft`
                                  : cloudStatus;
                        const currentCondition15m = prwxst1_15mnavg ?? prwxst2_15mnavg;

                        setData({
                            visibilityAverage1m: visibil_01mnavg,
                            visibilityAverage10m: visibil_10mnavg,
                            cloudStatus,
                            verboseCloudStatus,
                            qnhAverage1m: presqnh_01mnavg,
                            pressureSensorChange1h: pressen_01hrchg,
                            dewPointAverage1m: dewtemp_01mnavg,
                            temperatureAverage1m: airtemp_01mnavg,
                            currentCondition15m,
                            issueTime: obs_timestamp,
                        });
                    })
                    .catch((e) => {
                        console.warn(e);
                        if (abortController.signal.aborted === true) return;
                    });
            } catch (e) {
                console.error(e);
            } finally {
                timeoutHook = setTimeout(patientQuery, 55 * 1000) as unknown as number;
            }
        };

        patientQuery();

        return () => {
            abortController.abort();
            clearTimeout(timeoutHook);
        };
    }, [datasetId, stage, metserviceApi]);

    return (
        <div
            style={{
                width: '100%',
                height: '100%',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                font: '400 12px/18px Open Sans',
                minWidth: 250,
                color: theme.text,
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div style={toolbarStyle}>
                    <Title style={{ color: theme.text }}>{title}</Title>
                </div>
                <div style={{ display: 'flex', columnGap: 4 }}>
                    <IssueTime
                        issueTime={data.issueTime}
                        targetTimezone={targetTimezone}
                        isDarkMode={isDarkMode}
                        style={toolbarStyle}
                        colour={theme.issueTime}
                    />
                    <div style={toolbarStyle}>
                        <InfoPopover />
                    </div>
                </div>
            </div>
            <hr
                style={{
                    border: 'none',
                    borderBottom: '1px solid #cdcdcd',
                    margin: '8px 0px 0px 0px',
                    width: '100%',
                }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                {isLoading && (
                    <div style={{ display: 'flex', justifyContent: 'center', flex: 1 }}>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                flexDirection: 'column',
                            }}
                        >
                            <Spin style={{ marginTop: 12 }} />
                        </div>
                    </div>
                )}
                {!isLoading && (
                    <>
                        <Row>
                            <HeaderCell mode={settings.mode}>LOCATION</HeaderCell>
                            <TableCell mode={settings.mode}>{locationLabel}</TableCell>
                        </Row>
                        <Row>
                            <HeaderCell mode={settings.mode} style={{ minHeight: 60 }}>
                                STATE OF SKY
                            </HeaderCell>
                            <TableCell mode={settings.mode} style={{ minHeight: 60 }}>
                                <div>{!data.visibilityAverage1m ? 'N/A' : `VIS ${data.visibilityAverage1m}`}</div>
                                <div>{!data.currentCondition15m || data.currentCondition15m === '//' ? 'N/A' : `WX ${data.currentCondition15m}`}</div>
                                <div style={{ whiteSpace: 'normal' }}>{!data.cloudStatus ? 'N/A' : `CLD | ${data.cloudStatus}`}</div>
                            </TableCell>
                        </Row>
                        <Row>
                            <HeaderCell mode={settings.mode}>VISIBILITY 10MIN AVG</HeaderCell>
                            <TableCell mode={settings.mode}>{!data.visibilityAverage10m ? 'N/A' : `${data.visibilityAverage10m}`}</TableCell>
                        </Row>
                        <Row>
                            <HeaderCell mode={settings.mode}>CURRENT CONDITIONS</HeaderCell>
                            <TableCell mode={settings.mode}>
                                {!data.currentCondition15m || data.currentCondition15m === '//' ? 'N/A' : getGRAFORValue(data.currentCondition15m)}
                            </TableCell>
                        </Row>
                        <Row>
                            <HeaderCell mode={settings.mode}>CLOUD</HeaderCell>
                            <TableCell mode={settings.mode} style={{ whiteSpace: 'normal' }}>
                                {!data.verboseCloudStatus ? 'N/A' : data.verboseCloudStatus}
                            </TableCell>
                        </Row>
                        <Row>
                            <HeaderCell mode={settings.mode}>TEMPERATURE 1MIN AVG</HeaderCell>
                            <TableCell mode={settings.mode}>{!data.temperatureAverage1m ? 'N/A' : `${data.temperatureAverage1m} °C`}</TableCell>
                        </Row>
                        <Row>
                            <HeaderCell mode={settings.mode}>DEW POINT 1MIN AVG</HeaderCell>
                            <TableCell mode={settings.mode}>{!data.dewPointAverage1m ? 'N/A' : `${data.dewPointAverage1m} °C`}</TableCell>
                        </Row>
                        <Row>
                            <HeaderCell mode={settings.mode}>QNH 1MIN AVG</HeaderCell>
                            <TableCell mode={settings.mode}>{!data.qnhAverage1m ? 'N/A' : `${data.qnhAverage1m} hPa`}</TableCell>
                        </Row>
                        <Row style={{ borderBottom: 'none' }}>
                            <HeaderCell mode={settings.mode}>QNH TREND 1HR CHANGE</HeaderCell>
                            <TableCell mode={settings.mode}>{!data.pressureSensorChange1h ? 'N/A' : `${data.pressureSensorChange1h} hPa`}</TableCell>
                        </Row>
                    </>
                )}
            </div>
        </div>
    );
};
